/**
 * OnboardingPage — Hospital onboarding workspace (HON-001/003/004).
 *
 * Three surfaces:
 *   1. Onboarding wizard checklist (GET /portals/onboarding) — a vertical stepper
 *      with done / active / todo states + a progress bar.
 *   2. Client-users roster (GET /portals/client-users) with provisioning
 *      (POST /portals/client-users, HON-003).
 *   3. First-file validation (GET /portals/first-file) — schema check,
 *      completeness score, field mapping, and sample records (HON-004).
 *
 * Data via useOnboarding(); each surface degrades gracefully to a seed on 404.
 */
import { useState, type CSSProperties } from 'react';
import {
  useOnboarding,
  type ClientUser,
  type OnboardingTask,
} from '../hooks/useOnboarding';
import { colors, radius, space } from '../theme';
import { PageHeader } from '../components/PageHeader';
import { ThreePanel } from '../components/ThreePanel';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { RoleBadge } from '../components/RoleBadge';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { useShellSlot } from '../components/shellContext';

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString();
}

/** Map an onboarding task status to a stepper node treatment. */
function stepState(status: string): 'done' | 'active' | 'blocked' | 'todo' {
  const s = status.toLowerCase();
  if (s === 'complete' || s === 'completed' || s === 'done') return 'done';
  if (s === 'in_progress' || s === 'active') return 'active';
  if (s === 'blocked') return 'blocked';
  return 'todo';
}

export function OnboardingPage() {
  useShellSlot({ breadcrumb: 'Hospital Onboarding' });
  const {
    checklist,
    users,
    firstFile,
    checklistAvailable,
    usersAvailable,
    firstFileAvailable,
    isLoading,
    isWorking,
    error,
    actionError,
    refresh,
    provisionUser,
  } = useOnboarding();

  const [showProvision, setShowProvision] = useState(false);

  const tasks = checklist?.tasks ?? [];
  const percent = checklist?.percent_complete ?? 0;
  const anySeeded = !checklistAvailable || !usersAvailable || !firstFileAvailable;

  const userColumns: Column<ClientUser>[] = [
    {
      key: 'user',
      header: 'User',
      render: (u) => (
        <div>
          <div style={{ fontWeight: 600 }}>{u.name}</div>
          <div style={styles.muted}>{u.email}</div>
        </div>
      ),
    },
    { key: 'role', header: 'Role', render: (u) => <RoleBadge role={u.role} /> },
    { key: 'access', header: 'Access', render: (u) => <StatusBadge value={u.access_status} /> },
    { key: 'training', header: 'Training', render: (u) => <StatusBadge value={u.training_status} /> },
    { key: 'invited', header: 'Invited', render: (u) => fmtDate(u.invited_at) },
  ];

  return (
    <>
      <PageHeader
        title="Hospital Onboarding"
        subtitle="Stand up a new hospital tenant: work the onboarding checklist, provision portal users, and validate the first inbound census file (HON-001/003/004)."
        actions={
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
      />

      {anySeeded && (
        <p style={styles.seedNotice} role="status">
          Some onboarding surfaces are served from a seed until the portals service exposes them.
        </p>
      )}
      {error && (
        <p style={styles.error} role="alert">
          {error.message}
        </p>
      )}

      <ThreePanel
        primary={
          <>
            {/* First-file validation (HON-004) */}
            <Card title="First-file validation (HON-004)">
              {firstFile ? (
                <div style={styles.ffWrap}>
                  <div style={styles.ffHeader}>
                    <div>
                      <div style={{ fontWeight: 600 }}>{firstFile.file_name}</div>
                      <div style={styles.muted}>
                        {firstFile.record_count.toLocaleString()} records · received{' '}
                        {fmtDate(firstFile.received_at)}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <StatusBadge
                        value={firstFile.schema_valid ? 'schema valid' : 'schema invalid'}
                        status={firstFile.schema_valid ? 'success' : 'danger'}
                      />
                      <span style={styles.scoreChip}>
                        {Math.round(firstFile.completeness_score * 100)}% complete
                      </span>
                    </div>
                  </div>

                  <div style={styles.ffGrid}>
                    <div>
                      <h4 style={styles.subLabel}>Field mapping</h4>
                      <table style={styles.miniTable}>
                        <tbody>
                          {firstFile.field_checks.map((f) => (
                            <tr key={f.field}>
                              <td style={styles.miniTd}>
                                <code style={styles.code}>{f.field}</code>
                              </td>
                              <td style={styles.miniTd}>
                                {f.present ? (
                                  <span style={{ color: colors.success }}>→ {f.mapped_to ?? 'mapped'}</span>
                                ) : (
                                  <span style={{ color: colors.warning }}>missing</span>
                                )}
                                {f.note && <span style={styles.fieldNote}> · {f.note}</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div>
                      <h4 style={styles.subLabel}>Sample records</h4>
                      <div style={styles.sampleList}>
                        {firstFile.sample_records.map((r, i) => (
                          <pre key={i} style={styles.sample}>
                            {JSON.stringify(r, null, 0)}
                          </pre>
                        ))}
                      </div>
                    </div>
                  </div>

                  {firstFile.issues.length > 0 && (
                    <div style={styles.issues}>
                      <h4 style={styles.subLabel}>Issues</h4>
                      <ul style={styles.issueList}>
                        {firstFile.issues.map((iss, i) => (
                          <li key={i}>{iss}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <LoadingIndicator text="Loading file validation…" />
              )}
            </Card>

            {/* Client users (HON-003) */}
            <Card
              title="Hospital portal users (HON-003)"
              actions={
                <button type="button" style={styles.primaryBtnSm} onClick={() => setShowProvision(true)}>
                  + Provision user
                </button>
              }
              padded={false}
            >
              <DataTable
                columns={userColumns}
                rows={users}
                rowKey={(u) => u.user_id}
                emptyTitle="No portal users yet"
                emptyDescription="Provision a CFO viewer or hospital admin to grant portal access."
              />
            </Card>
          </>
        }
        rightContext={
          <>
            <h3 style={styles.contextTitle}>Onboarding checklist</h3>
            <div style={styles.progressWrap}>
              <div style={styles.progressBar}>
                <div style={{ ...styles.progressFill, width: `${percent}%` }} />
              </div>
              <div style={styles.progressLabel}>
                {checklist?.completed ?? 0} / {checklist?.total ?? tasks.length} complete ·{' '}
                {Math.round(percent)}%
              </div>
            </div>
            <ol style={styles.stepper}>
              {tasks.map((t, i) => (
                <Step key={t.key} task={t} index={i} last={i === tasks.length - 1} />
              ))}
            </ol>
          </>
        }
      />

      {showProvision && (
        <ProvisionDialog
          isWorking={isWorking}
          error={actionError?.message ?? null}
          onClose={() => setShowProvision(false)}
          onSubmit={async (body) => {
            const ok = await provisionUser(body);
            if (ok) setShowProvision(false);
          }}
        />
      )}
    </>
  );
}

function Step({ task, index, last }: { task: OnboardingTask; index: number; last: boolean }) {
  const state = stepState(task.status);
  const node = NODE_STYLE[state];
  return (
    <li style={styles.step}>
      <div style={styles.stepLeft}>
        <span style={{ ...styles.stepNode, ...node.circle }}>{state === 'done' ? '✓' : index + 1}</span>
        {!last && <span style={{ ...styles.stepLine, background: state === 'done' ? colors.success : colors.border }} />}
      </div>
      <div style={styles.stepBody}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: state === 'active' ? 700 : 600, fontSize: '0.88rem' }}>
            {task.title}
          </span>
          <StatusBadge value={task.status} />
        </div>
        {task.description && <div style={styles.stepDesc}>{task.description}</div>}
        {task.owner && <div style={styles.stepOwner}>Owner: {task.owner}</div>}
      </div>
    </li>
  );
}

function ProvisionDialog({
  isWorking,
  error,
  onClose,
  onSubmit,
}: {
  isWorking: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (body: { email: string; name: string; role: string }) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('cfo_viewer');
  const valid = name.trim() && email.includes('@');

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Provision portal user">
      <div style={styles.dialog}>
        <h3 style={styles.dialogTitle}>Provision portal user</h3>
        <label style={styles.field}>
          <span style={styles.label}>Full name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} style={styles.input} />
        </label>
        <label style={styles.field}>
          <span style={styles.label}>Email</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} style={styles.input} type="email" />
        </label>
        <label style={styles.field}>
          <span style={styles.label}>Portal role</span>
          <select value={role} onChange={(e) => setRole(e.target.value)} style={styles.input}>
            <option value="cfo_viewer">CFO viewer</option>
            <option value="hospital_admin">Hospital admin</option>
            <option value="navigator">Navigator</option>
          </select>
        </label>
        {error && (
          <p style={styles.error} role="alert">
            {error}
          </p>
        )}
        <div style={styles.dialogActions}>
          <button type="button" style={styles.smallBtn} onClick={onClose} disabled={isWorking}>
            Cancel
          </button>
          <button
            type="button"
            style={styles.primaryBtn}
            disabled={isWorking || !valid}
            onClick={() => onSubmit({ name: name.trim(), email: email.trim(), role })}
          >
            {isWorking ? 'Provisioning…' : 'Send invitation'}
          </button>
        </div>
      </div>
    </div>
  );
}

const NODE_STYLE: Record<string, { circle: CSSProperties }> = {
  done: { circle: { background: colors.success, color: colors.white, borderColor: colors.success } },
  active: { circle: { background: colors.action, color: colors.white, borderColor: colors.action } },
  blocked: { circle: { background: colors.danger, color: colors.white, borderColor: colors.danger } },
  todo: { circle: { background: colors.white, color: colors.textMuted, borderColor: colors.border } },
};

const styles: Record<string, CSSProperties> = {
  seedNotice: {
    margin: '0 0 16px',
    padding: '8px 12px',
    background: colors.brandTint,
    color: colors.brandActive,
    borderRadius: radius.sm,
    fontSize: '0.82rem',
  },
  error: { color: colors.danger, fontSize: '0.9rem', margin: '0 0 12px' },
  muted: { color: colors.textSecondary, fontSize: '0.82rem' },
  contextTitle: { margin: 0, fontSize: '0.95rem', fontWeight: 700, color: colors.textPrimary },
  progressWrap: { display: 'flex', flexDirection: 'column', gap: 6 },
  progressBar: {
    height: 8,
    borderRadius: radius.pill,
    background: colors.surfaceAlt,
    overflow: 'hidden',
  },
  progressFill: { height: '100%', background: colors.action, borderRadius: radius.pill },
  progressLabel: { fontSize: '0.78rem', color: colors.textSecondary },
  stepper: { listStyle: 'none', margin: '8px 0 0', padding: 0 },
  step: { display: 'flex', gap: 12 },
  stepLeft: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
  stepNode: {
    width: 26,
    height: 26,
    borderRadius: '50%',
    border: '2px solid',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.78rem',
    fontWeight: 700,
    flexShrink: 0,
  },
  stepLine: { width: 2, flex: 1, minHeight: 14, margin: '2px 0' },
  stepBody: { paddingBottom: 16, minWidth: 0 },
  stepDesc: { fontSize: '0.8rem', color: colors.textSecondary, marginTop: 3, lineHeight: 1.4 },
  stepOwner: { fontSize: '0.72rem', color: colors.textMuted, marginTop: 3 },
  ffWrap: { display: 'flex', flexDirection: 'column', gap: space.md },
  ffHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' },
  scoreChip: {
    background: colors.brandTint,
    color: colors.brandActive,
    borderRadius: radius.pill,
    padding: '2px 10px',
    fontSize: '0.74rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  ffGrid: { display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1fr)', gap: space.lg },
  subLabel: { fontSize: '0.82rem', margin: '0 0 8px', color: colors.textSecondary, fontWeight: 600 },
  miniTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' },
  miniTd: { padding: '4px 6px', borderBottom: `1px solid ${colors.border}`, verticalAlign: 'top' },
  code: { background: colors.surface, padding: '1px 5px', borderRadius: 3, fontSize: '0.78rem' },
  fieldNote: { color: colors.textMuted, fontSize: '0.74rem' },
  sampleList: { display: 'flex', flexDirection: 'column', gap: 6 },
  sample: {
    margin: 0,
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    padding: '6px 8px',
    fontSize: '0.72rem',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  issues: {
    background: '#FEF3C7',
    border: '1px solid #FDE68A',
    borderRadius: radius.sm,
    padding: '10px 12px',
  },
  issueList: { margin: 0, paddingLeft: 18, fontSize: '0.82rem', color: colors.textPrimary },
  smallBtn: {
    padding: '5px 12px',
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  primaryBtnSm: {
    padding: '5px 12px',
    background: colors.action,
    color: colors.white,
    border: 'none',
    borderRadius: radius.sm,
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  primaryBtn: {
    padding: '7px 16px',
    background: colors.action,
    color: colors.white,
    border: 'none',
    borderRadius: radius.sm,
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28,25,23,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  },
  dialog: {
    background: colors.white,
    borderRadius: radius.lg,
    padding: 24,
    width: 'min(420px, 92vw)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  dialogTitle: { margin: 0, fontSize: '1.1rem', fontWeight: 700 },
  dialogActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: '0.82rem', fontWeight: 600, color: colors.textSecondary },
  input: {
    padding: '8px 10px',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    fontSize: '0.92rem',
  },
};
